/**
 * ENG-10140 — is this host's `/root/.claude.json` one a container can safely be
 * started against?
 *
 * WHAT THIS FILE IS NOT. The ticket asked for "an atomic, validating writer for
 * /root/.claude.json matching ENG-4787's treatment of .mcp.json". That cannot be
 * built, and the reason is worth stating once here so nobody re-derives it:
 *
 *   WE NEVER WRITE THAT FILE. An exhaustive search of apps/cli, packages/core
 *   and packages/api finds no write and no unlink of `.claude.json` - only
 *   reads (claude-account-fingerprint, claude-code-updater, claude-pair-runtime).
 *   `claude-pair-runtime` says so outright, polling mtime because "claude's
 *   exact write timing is version-dependent". ENG-4787's remedy applied to
 *   `.mcp.json` because the manager writes `.mcp.json`. Here the writer is
 *   Claude Code itself, so there is no call site to make atomic.
 *
 *   And a rename-based writer would not work even if there were one: a rename
 *   over a bind-mount target fails EBUSY (persistent-session.ts, verified on
 *   agt-runtime 2026-09-11). `atomicWriteFileSync` renames. Pointing it at this
 *   path would throw on any host with a running agent container.
 *
 * SO THIS DETECTS INSTEAD OF WRITING, and it detects TWO DISTINCT FAULTS that
 * the ticket treats as one. Both were observed; neither is hypothetical.
 *
 * 1. ORPHANED MOUNT. `/root/.claude.json` is bind-mounted into every agent
 *    container as a FILE. Unlink of a bind-mount source is permitted (unlike
 *    rename over the target, which is EBUSY), so when a host-side process
 *    replaces the file, every running container keeps the old inode - alive only
 *    by its own mount, with no directory entry on the host.
 *
 *    Observed live on a production host: mountinfo carrying
 *    `/root/.claude.json//deleted`, `nlink=0`, while sibling files under the
 *    same mounts read `nlink=1`. The orphan's inode was born 35 seconds BEFORE
 *    that container started and was still being written 5.5 hours later - to a
 *    file the host could no longer see.
 *
 *    THE SYMPTOM IS NOT A PARSE ERROR. An orphaned config parses perfectly; it
 *    is simply stale and private. The agent's own writes die with the container,
 *    every host-side update (INCLUDING A RE-AUTH) is invisible to it, and when
 *    Claude Code next cannot validate its session it falls to the LOGIN PICKER
 *    (see claude-dialogs.ts) - while the host's `claude_auth_status` reads
 *    valid, because the HOST's file is fine. That is the ticket's otherwise
 *    unexplained case: a host reporting 7 hours of validity with its agents
 *    face-down. Anything that checks only JSON validity will call this healthy.
 *
 * 2. TRUNCATED CONTENT. The corrupt instances in the ledger are 160 and 173
 *    bytes against a ~52KB file, plus one zero-byte case. Orphaning cannot
 *    produce these: an orphaned inode keeps whatever it held. These are a
 *    container binding the NEW inode while a host-side write is still in
 *    flight, and reading the partial.
 *
 * The two want different responses, which is why `assessClaudeConfig` returns a
 * verdict rather than a boolean: a truncated file must block a spawn (starting
 * Claude Code against it lands the agent on the login picker, where it sits),
 * whereas an orphan is about a container that is ALREADY running and must be
 * reported and recycled, not blocked.
 */

/** What we found. Ordered roughly by how bad it is. */
export type ClaudeConfigVerdict =
  /** Parses, plausible shape, mount not orphaned. */
  | 'ok'
  /** File absent entirely - claude will show the login picker on first start. */
  | 'missing'
  /**
   * The file EXISTS but could not be read (EACCES, EISDIR, EIO...). A different
   * fault with a different remedy, and it must never be reported as `missing`.
   */
  | 'unreadable'
  /** Zero bytes. The 29 Aug instance in the ledger. */
  | 'empty'
  /** Present and non-empty but not JSON - the "Unexpected EOF" instances. */
  | 'unparseable'
  /** Valid JSON, but far too small to be a real config: a partial write. */
  | 'truncated'
  /** Parses fine and is the right size, but the container's mount is orphaned. */
  | 'orphaned';

export interface ClaudeConfigAssessment {
  verdict: ClaudeConfigVerdict;
  /** One line, safe to log: never contains file contents. */
  detail: string;
  /** True when starting Claude Code against this would strand the agent. */
  blocksSpawn: boolean;
}

/**
 * The smallest byte count a REAL `.claude.json` can plausibly have.
 *
 * The live file on a working host is ~52KB across 46 top-level keys, most of
 * them counters and caches Claude Code rewrites constantly. The corrupt
 * instances in the ledger are 160 and 173 bytes. 1024 sits an order of
 * magnitude above the corrupt cases and one and a half below the healthy one,
 * so it separates them without being tuned to either.
 *
 * Deliberately a SIZE floor and not a key check: the key set is Claude Code's,
 * it changes between versions, and a guard that asserts someone else's schema
 * becomes a false alarm on their next release.
 */
export const MIN_PLAUSIBLE_CLAUDE_JSON_BYTES = 1024;

/** The inputs, injected so this is testable without a filesystem or a host. */
export interface ClaudeConfigProbe {
  /** Raw file contents, or null when the file could not be read at all. */
  raw: string | null;
  /**
   * Why `raw` is null, when it is: `'missing'` for ENOENT, `'unreadable'` for
   * any other read error.
   *
   * CodeRabbit on #6321. The probe used to collapse every read failure to
   * `raw = null`, which `assessClaudeConfig` reported as `missing` - "does not
   * exist, Claude Code will show the login picker". For EACCES or EISDIR that is
   * the WRONG REMEDY, and pointing an operator at sign-in when the real fault is
   * a permission bit is precisely the misdiagnosis this whole module exists to
   * stop. Optional so existing callers keep working; absent is read as `missing`
   * only because that is what a null `raw` meant before this field existed.
   */
  readFailure?: 'missing' | 'unreadable';
  /** `st_nlink`. Zero means the inode has no directory entry: orphaned. */
  nlink: number | null;
  /** Contents of /proc/self/mountinfo, or null when unavailable. */
  mountinfo: string | null;
  /** The path being assessed, e.g. `/root/.claude.json`. */
  path: string;
}

/**
 * Does `mountinfo` show `path` bind-mounted from a source the kernel has marked
 * deleted?
 *
 * The kernel appends `//deleted` to the mount ROOT field (field 4) when the
 * source dentry is unlinked. Matching on the mount-point field as well as the
 * root keeps an unrelated deleted mount elsewhere on the host from being read
 * as this one.
 */
export function hasOrphanedMount(mountinfo: string | null, path: string): boolean {
  if (!mountinfo) return false;
  for (const line of mountinfo.split('\n')) {
    if (!line.includes('//deleted')) continue;
    const fields = line.split(' ');
    // mountinfo: id parentId major:minor root mountPoint ...
    const root = fields[3];
    const mountPoint = fields[4];
    if (mountPoint !== path) continue;
    if (root === `${path}//deleted`) return true;
  }
  return false;
}

/**
 * Assess a host's `.claude.json` before binding it into a container.
 *
 * Order matters: content faults are checked BEFORE the orphan check, because a
 * truncated file is the more urgent answer and because an orphaned mount whose
 * contents are also broken should report the content fault (that is what stops
 * the spawn).
 */
export function assessClaudeConfig(probe: ClaudeConfigProbe): ClaudeConfigAssessment {
  const { raw, nlink, mountinfo, path, readFailure } = probe;

  if (raw === null) {
    if (readFailure === 'unreadable') {
      return {
        verdict: 'unreadable',
        detail:
          `${path} EXISTS but could not be read (permissions, or it is not a regular file). ` +
          `This is not a sign-in problem and re-authenticating will not fix it.`,
        blocksSpawn: true,
      };
    }
    return {
      verdict: 'missing',
      detail: `${path} does not exist - Claude Code will show the login picker`,
      blocksSpawn: true,
    };
  }
  if (raw.length === 0) {
    return { verdict: 'empty', detail: `${path} is zero bytes`, blocksSpawn: true };
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {
        verdict: 'unparseable',
        detail: `${path} parsed but is not a JSON object`,
        blocksSpawn: true,
      };
    }
  } catch {
    // THE PARSE ERROR IS DELIBERATELY DISCARDED, and the first version of this
    // file got that wrong (CodeRabbit, #6321).
    //
    // It passed `err.message` through on the reasoning that Node's message
    // "names the fault without quoting the file". That is false for one of
    // V8's formatters: an unexpected-token error echoes a window of the INPUT
    // back. Verified on Node v22.23.2 -
    //
    //   JSON.parse('{"oauthAccount":"SECRET-abc123", "x": <}')
    //   -> Unexpected token '<', ..."23", "x": <}" is not valid JSON
    //
    // `.claude.json` is 0600 and holds `oauthAccount`, `userID` and
    // `machineID`, and this string is returned to `persistent-session`, which
    // logs it unredacted into manager.log. A truncated config whose break lands
    // mid-credential would put that fragment in the log - a guard for a
    // corruption bug becoming a credential leak, which is strictly worse than
    // the bug.
    //
    // The byte count is kept because it is the diagnostic that matters (it is
    // how the 160/173-byte signatures were identified in the first place) and
    // it cannot carry content.
    return {
      verdict: 'unparseable',
      detail: `${path} is not valid JSON (parse error) at ${raw.length} bytes`,
      blocksSpawn: true,
    };
  }

  if (raw.length < MIN_PLAUSIBLE_CLAUDE_JSON_BYTES) {
    return {
      verdict: 'truncated',
      detail:
        `${path} parses but is only ${raw.length} bytes, below the ` +
        `${MIN_PLAUSIBLE_CLAUDE_JSON_BYTES}-byte floor - a partial write, not a real config`,
      blocksSpawn: true,
    };
  }

  // Content is fine. Is this process reading a file the host still has?
  if (nlink === 0 || hasOrphanedMount(mountinfo, path)) {
    return {
      verdict: 'orphaned',
      detail:
        `${path} is bind-mounted from an UNLINKED inode (nlink=${nlink ?? 'unknown'}) - ` +
        `the host replaced this file after the container started. Config writes here are ` +
        `invisible to the host and host-side updates, including re-auth, are invisible here. ` +
        `Recycle the container; do not treat this as an auth failure.`,
      // Deliberately NOT blocking: by the time this is observable the container
      // is already running, and refusing to spawn is the wrong lever. The fix
      // is a recycle, and the point of the verdict is that somebody is told.
      blocksSpawn: false,
    };
  }

  return { verdict: 'ok', detail: `${path} is valid (${raw.length} bytes)`, blocksSpawn: false };
}

/**
 * Probe the real filesystem and return a log-ready sentence, or null when the
 * config is fine and the caller should use its own wording.
 *
 * Separated from `assessClaudeConfig` so the decision stays pure and testable
 * and only this thin shell touches the disk. Never throws: it is called from a
 * path that has ALREADY decided the agent is blocked, and a failure to explain
 * why must not become a second failure.
 */
/**
 * ENG-10869: takes the `.claude.json` FILE path, not a home dir.
 *
 * It used to build `<homeDir>/.claude.json` itself, and that became wrong the
 * moment the file moved into the config dir: the diagnostic would have
 * described the legacy file - quite possibly reporting it healthy - while the
 * agent read a different one. That is the same look-at-the-wrong-file fault
 * this module exists to detect, in the tool an operator reads DURING the
 * incident. The caller resolves the path through `claude-config-location`, so
 * there is one answer rather than two.
 */
export function describeClaudeConfigForLog(claudeJsonFilePath: string): string | null {
  try {
    // Imported lazily so the pure module above stays free of node:fs for its
    // own tests and for any future non-node consumer.
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { readFileSync, statSync } = require('node:fs') as typeof import('node:fs');
    const path = claudeJsonFilePath;

    let raw: string | null = null;
    let readFailure: 'missing' | 'unreadable' | undefined;
    try {
      raw = readFileSync(path, 'utf8');
    } catch (err) {
      // ONLY ENOENT is "missing". Everything else - EACCES, EISDIR, EIO - means
      // the file is there and we could not read it, which has a different
      // remedy. Collapsing them was CodeRabbit's finding on #6321.
      raw = null;
      readFailure = (err as NodeJS.ErrnoException)?.code === 'ENOENT' ? 'missing' : 'unreadable';
    }
    let nlink: number | null = null;
    try { nlink = statSync(path).nlink; } catch { nlink = null; }
    let mountinfo: string | null = null;
    try { mountinfo = readFileSync('/proc/self/mountinfo', 'utf8'); } catch { mountinfo = null; }

    const out = assessClaudeConfig({ raw, nlink, mountinfo, path, readFailure });
    if (out.verdict === 'ok') return null;
    return `${out.verdict}: ${out.detail}`;
  } catch {
    return null;
  }
}
